/**
 * Retours (signaler un problème / proposer une idée) — règles partagées API /
 * interface (sous-chemin `@campus/core/feedback` côté serveur, jamais le
 * barrel : piège Phaser). Voir tasks/retours-plan.md.
 */

export const FEEDBACK_KINDS = ['bug', 'idea'] as const;
export type FeedbackKind = typeof FEEDBACK_KINDS[number];

export const FEEDBACK_STATUSES = ['new', 'acknowledged', 'resolved', 'dismissed'] as const;
export type FeedbackStatus = typeof FEEDBACK_STATUSES[number];

export const FEEDBACK_KIND_LABELS: Record<FeedbackKind, string> = { bug: 'Problème', idea: 'Idée' };
export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'Nouveau',
  acknowledged: 'Pris en compte',
  resolved: 'Résolu',
  dismissed: 'Écarté',
};

export const FEEDBACK_MESSAGE_MIN = 10;
export const FEEDBACK_MESSAGE_MAX = 4000;
/** Capture envoyée par le navigateur (data URL décodée), avant réencodage. */
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 4 * 1024 * 1024;
/** Envois par personne et par heure. */
export const FEEDBACK_RATE_LIMIT = { max: 5, windowMs: 60 * 60 * 1000 };

/**
 * Contexte joint automatiquement par le navigateur — informatif seulement
 * (non vérifié), filtré et borné par le serveur (`sanitizeFeedbackContext`).
 */
export interface FeedbackContext {
  map?: string;
  mapName?: string;
  tileX?: number;
  tileY?: number;
  bubble?: string;
  userAgent?: string;
  platform?: string;
  language?: string;
  screen?: string;
  viewport?: string;
  appVersion?: string;
  url?: string;
}

const STRING_KEYS = ['map', 'mapName', 'bubble', 'userAgent', 'platform', 'language', 'screen', 'viewport', 'appVersion', 'url'] as const;
const NUMBER_KEYS = ['tileX', 'tileY'] as const;
const MAX_FIELD = 300;

export const isFeedbackKind = (v: unknown): v is FeedbackKind => (FEEDBACK_KINDS as readonly unknown[]).includes(v);
export const isFeedbackStatus = (v: unknown): v is FeedbackStatus => (FEEDBACK_STATUSES as readonly unknown[]).includes(v);

/** Message nettoyé (espaces de bord), ou null s'il est trop court / trop long. */
export function normalizeFeedbackMessage(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const text = v.trim();
  return text.length >= FEEDBACK_MESSAGE_MIN && text.length <= FEEDBACK_MESSAGE_MAX ? text : null;
}

/** Ne garde que les champs connus, de bon type, tronqués. */
export function sanitizeFeedbackContext(raw: unknown): FeedbackContext {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: Record<string, string | number> = {};
  for (const k of STRING_KEYS) {
    const v = src[k];
    if (typeof v === 'string' && v.trim()) out[k] = v.trim().slice(0, MAX_FIELD);
  }
  for (const k of NUMBER_KEYS) {
    const v = src[k];
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = Math.round(v);
  }
  return out as FeedbackContext;
}
